import {
    INITIAL_FROM_AMOUNT,
    INITIAL_FROM_CURRENCY,
    INITIAL_RATES_FROM_TO_NOK,
    INITIAL_TO_CURRENCY,
} from '../constants';
import {
    Calculator,
    CalculatorFieldName,
    CommonCurrency,
    CurrencyDetails,
    ExchangeRate,
    ExchangeRateResult,
    FetchError,
} from '../types';
import { getCalculatedCurrencyAmount, getCurrencyDetailsFromCurrencyCode } from './type-functions';

export const getAlreadyFetchedBothWayExchangeRateFromCurrencies = (
    from: CurrencyDetails,
    to: CurrencyDetails,
    fetchedRates: ExchangeRateResult[],
): ExchangeRate => {
    const fromTo = fetchedRates.find((rate) => from.currencyCode === rate.from && to.currencyCode === rate.to);
    const toFrom = fetchedRates.find((rate) => from.currencyCode === rate.to && to.currencyCode === rate.from);

    return {
        from: fromTo,
        to: toFrom,
    };
};

export const swap = (
    calculator: Calculator,
    setCalculator: (calculator: Calculator) => void,
    setFocusedField: (value: CalculatorFieldName | undefined) => void,
) => {
    setFocusedField(undefined);
    const updatedCalculator: Calculator = {
        ...calculator,
        from: calculator.to,
        to: calculator.from,
        exchangeRate: {
            from: calculator.exchangeRate.to,
            to: calculator.exchangeRate.from,
        },
    };

    setCalculator(updatedCalculator);
};

const createExchangeRateObject = (
    calculator: Calculator,
    selectedCurrency: CurrencyDetails,
    exchangeRate: ExchangeRate,
    usingFromField: boolean,
    setFocusedField: (value: CalculatorFieldName | undefined) => void,
) => {
    // When user changes TO currency we reset the FROM amount to 1
    const RESET_AMOUNT_VALUE = 1;
    const CALC_FROM_AMOUNT = RESET_AMOUNT_VALUE;

    const updatedObject = usingFromField
        ? {
              from: {
                  ...calculator.from,
                  currency: selectedCurrency,
              },
              to: {
                  ...calculator.to,
                  amount: getCalculatedCurrencyAmount(calculator.from.amount, exchangeRate.from),
              },
          }
        : {
              from: {
                  ...calculator.from,
                  amount: CALC_FROM_AMOUNT,
              },
              to: {
                  ...calculator.to,
                  amount: getCalculatedCurrencyAmount(CALC_FROM_AMOUNT, exchangeRate.from),
                  currency: selectedCurrency,
              },
          };

    setFocusedField(CalculatorFieldName.FROM);

    return updatedObject;
};

const currenciesAlreadyFetched = (
    fromCurrency: CurrencyDetails,
    toCurrency: CurrencyDetails,
    fetchedRates: ExchangeRateResult[],
) => {
    const { from, to } = getAlreadyFetchedBothWayExchangeRateFromCurrencies(fromCurrency, toCurrency, fetchedRates);

    return typeof from !== 'undefined' && typeof to !== 'undefined';
};

export const getInitialCalculatorData = (
    availableCurrencies: CurrencyDetails[],
    fetchedRates: ExchangeRateResult[],
): Calculator | undefined => {
    const currentRate = fetchedRates.find(
        (rate) => rate.from === INITIAL_FROM_CURRENCY && rate.to === INITIAL_TO_CURRENCY,
    );

    const currencyDetailsFrom = getCurrencyDetailsFromCurrencyCode(INITIAL_FROM_CURRENCY, availableCurrencies);
    const currencyDetailsTo = getCurrencyDetailsFromCurrencyCode(INITIAL_TO_CURRENCY, availableCurrencies);

    if (
        typeof currentRate === 'undefined' ||
        typeof currencyDetailsFrom === 'undefined' ||
        typeof currencyDetailsTo === 'undefined'
    ) {
        return undefined;
    }

    return {
        from: {
            amount: INITIAL_FROM_AMOUNT,
            currency: currencyDetailsFrom,
        },
        to: {
            amount: getCalculatedCurrencyAmount(INITIAL_FROM_AMOUNT, currentRate),
            currency: currencyDetailsTo,
        },
        exchangeRate: getAlreadyFetchedBothWayExchangeRateFromCurrencies(
            currencyDetailsFrom,
            currencyDetailsTo,
            fetchedRates,
        ),
        rateAt: undefined,
    };
};

export const setAmountFrom = (
    value: number | undefined,
    calculator: Calculator,
    setCalculator: (calculator: Calculator) => void,
) => {
    const { from, to } = calculator;

    setCalculator({
        ...calculator,
        from: {
            ...from,
            amount: value,
        },
        to: {
            ...to,
            amount: getCalculatedCurrencyAmount(value, calculator.exchangeRate.from),
        },
    });
};

export const setAmountTo = (
    value: number | undefined,
    calculator: Calculator,
    setCalculator: (calculator: Calculator) => void,
) => {
    const { from, to } = calculator;

    setCalculator({
        ...calculator,
        from: {
            ...from,
            amount: getCalculatedCurrencyAmount(value, calculator.exchangeRate.to),
        },
        to: {
            ...to,
            amount: value,
        },
    });
};

export const getInitialRates = (
    getRateBothWays: (currencyCode1: string, currencyCode2: string, rateAt?: string) => Promise<ExchangeRateResult[]>,
): Promise<ExchangeRateResult[][]> =>
    Promise.all(INITIAL_RATES_FROM_TO_NOK.map(async (rate) => getRateBothWays(CommonCurrency.NOK, rate)));

export const changeCurrency = (
    calculator: Calculator,
    fetchedRates: ExchangeRateResult[],
    field: CalculatorFieldName,
    selectedCurrency: CurrencyDetails | undefined,
    setCalculator: (value: Calculator) => void,
    setFetchedRates: (value: ExchangeRateResult[]) => void,
    setFocusedField: (value: CalculatorFieldName | undefined) => void,
    setLoadingRate: (value: boolean) => void,
    setRateError: (value: string | undefined) => void,
    getRateBothWays: (currencyCode1: string, currencyCode2: string, rateAt?: string) => Promise<ExchangeRateResult[]>,
) => {
    if (typeof selectedCurrency === 'undefined') {
        return;
    }

    setRateError(undefined);

    const usingFromField = field === CalculatorFieldName.FROM;

    setFocusedField(field);

    const fromCurrency = usingFromField ? selectedCurrency : calculator.from.currency;
    const toCurrency = usingFromField ? calculator.to.currency : selectedCurrency;

    if (currenciesAlreadyFetched(fromCurrency, toCurrency, fetchedRates)) {
        const foundExchangeRate = getAlreadyFetchedBothWayExchangeRateFromCurrencies(
            fromCurrency,
            toCurrency,
            fetchedRates,
        );

        setCalculator({
            ...calculator,
            ...createExchangeRateObject(
                calculator,
                selectedCurrency,
                foundExchangeRate,
                usingFromField,
                setFocusedField,
            ),
            exchangeRate: foundExchangeRate,
        });

        return;
    }

    setLoadingRate(true);

    getRateBothWays(fromCurrency.currencyCode, toCurrency.currencyCode)
        .then((rateResult) => {
            setLoadingRate(false);
            setFetchedRates([...fetchedRates, ...rateResult]);
            const [fromTo, toFrom] = rateResult;

            const exchangeRate = {
                from: fromTo,
                to: toFrom,
            };

            setCalculator({
                ...calculator,
                ...createExchangeRateObject(
                    calculator,
                    selectedCurrency,
                    exchangeRate,
                    usingFromField,
                    setFocusedField,
                ),
                exchangeRate,
            });
        })
        .catch((error: FetchError) => {
            setLoadingRate(false);
            setRateError(error.errorName);
        });
};
